/**
 * Keep non-critical API calls off the first-paint path.
 *
 * `afterCriticalContent(fn)` runs `fn` once (a) the page's main content request
 * has settled (`markCriticalContentSettled`, called by the Home pulse strip) or
 * `maxWaitMs` has passed, whichever is first, and then (b) the browser is idle.
 * It returns a cancel function; a cancelled call never runs `fn`. Errors from
 * `fn` are swallowed: a deferred call must never block or break anything.
 */

let settleCriticalContent;
const criticalContentSettled = new Promise((resolve) => {
  settleCriticalContent = resolve;
});

export function markCriticalContentSettled() {
  settleCriticalContent();
}

// ponytail: Safari has no requestIdleCallback, so fall back to a short timer.
// The critical-content gate already does the real ordering; idle is a bonus.
export function runWhenIdle(fn, { timeout = 2000, fallbackDelayMs = 50 } = {}) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(fn, { timeout });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = setTimeout(fn, fallbackDelayMs);
  return () => clearTimeout(id);
}

export function afterCriticalContent(fn, { maxWaitMs = 4000 } = {}) {
  let cancelled = false;
  let started = false;
  let cancelIdle = () => {};

  const start = () => {
    if (cancelled || started) return;
    started = true;
    clearTimeout(maxWaitTimer);
    cancelIdle = runWhenIdle(() => {
      if (cancelled) return;
      try {
        Promise.resolve(fn()).catch(() => {});
      } catch {
        // deferred work is best-effort
      }
    });
  };

  const maxWaitTimer = setTimeout(start, maxWaitMs);
  criticalContentSettled.then(start);

  return () => {
    cancelled = true;
    clearTimeout(maxWaitTimer);
    cancelIdle();
  };
}
